import React, {
    FunctionComponent,
    useState,
    useRef,
} from 'react';
import {
    Formik,
    FormikHelpers,
    FormikProps,
    Form as FormikForm
} from 'formik';

// MUI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Snackbar from '@mui/material/Snackbar';
import LinearProgress from '@mui/material/LinearProgress';

// Common
import api from '../../api';
import { GENERAL } from '../../constants';
import { types } from '../../types';

// Components
import { FormPersonal } from './components/FormPersonal';
import { FormSchool } from './components/FormSchool';
import { FormTerms } from './components/FormTerms';
import { Alert } from './components/Alert';

// Properties
import { validationSchema, defaultValues } from './Form.validation';
import * as styles from './Form.styles';
import {TCheckValues, TTeacher} from "../../types/types";

const Form: FunctionComponent = () => {
    const [ isSnackBarOpen, setIsSnackBarOpen ] = useState(false);
    const [ isSaveError, setIsSaveError ] = useState(false);
    const [ loader, setLoader ] = useState(false);
    const [checkValues, setCheckValues] = useState<TCheckValues[]>([]);
    const [formErrors, setFormErrors] = useState<object>({});
    const formRef = useRef<FormikProps<types.TTeacher>>() as any;

    const postTeacher = (model: types.TTeacher) => {
        api.postTeacher(model)
            .then(() => {
                setIsSnackBarOpen(true);
                setIsSaveError(false);
                setLoader(false);
            })
            .catch(() => {
                setLoader(false);
                setIsSaveError(true);
            });
    };

    const teacherCardSchoolsValidation = (formValues: TTeacher) => {
        setFormErrors({});
        if(formValues.teacherCardSchools.length === 0){
            setFormErrors((prevFormErrors) => ({
                ...prevFormErrors,
                ['school'] : GENERAL.PERSONAL_FORM_VALIDATORS_TEXT.school,
            }));
            return;
        }

        formValues.teacherCardSchools.map((school) => {
            if(!school.subjects || school.subjects.length === 0){
                setFormErrors((prevFormErrors) => ({
                    ...prevFormErrors,
                    [`subjects-${school.id}`] : GENERAL.SCHOOL_SUBJECT_ERROR,
                    [`publisherId-${school.id}`]: GENERAL.PERSONAL_FORM_VALIDATORS_TEXT.publishersRequired,
                }));
                return;
            } else {
                school.subjects.forEach((subject) => {
                    if(subject.id === GENERAL.SUBJECT_EARLY_SCHOOL_EDUCATION && subject.earlySchoolEducationLevel == null){
                        setFormErrors((prevFormErrors) => ({
                            ...prevFormErrors,
                            [`earlySchoolEducationLevel-${school.id}`]: GENERAL.PERSONAL_LEVEL_VALIDATION,
                        }));
                    }
                    if(subject.id !== GENERAL.SUBJECT_OTHER && !subject.publisherId){
                        setFormErrors((prevFormErrors) => ({
                            ...prevFormErrors,
                            [`publisherId-${school.id}`]: GENERAL.PERSONAL_FORM_VALIDATORS_TEXT.publishersRequired,
                        }));
                    }
                    return;
                })
            }
            if(school.function){
                if(school.function.id === Number(GENERAL.FUNCTION_HEAD_OF_THE_SUBJECT_TEAM)){
                    if(!school.function.subjectId || school.function.subjectId === ''){
                        setFormErrors((prevFormErrors) => ({
                            ...prevFormErrors,
                            [`headOfTheSubjectTeam-${school.id}`]: GENERAL.SCHOOL_HEAD_OF_THE_SUBJECT_TEAM,
                        }));
                        return;
                    }
                }
            }
        })
    }

    const onSubmit = ({...values
    }: types.TTeacher) => {
        const preparePayload = {
            ...values,
        };
        postTeacher(preparePayload);
    };

    const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
        if (reason === 'clickaway') return;
        window.location.reload();
        setIsSnackBarOpen(false);
    };

    const handleClickAway = () => {
        window.location.reload();
        setIsSnackBarOpen(false);
    };

    return (
        <Formik
            innerRef={formRef}
            enableReinitialize
            initialValues={defaultValues}
            validationSchema={validationSchema}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={(
                values: types.TTeacher,
                formikHelpers: FormikHelpers<types.TTeacher>
            ) => {
                onSubmit(values);
                setLoader(true);
                formikHelpers.setSubmitting(false);
            }}
            validate={(values) => teacherCardSchoolsValidation(values)}
        >
            {(formikProps: FormikProps<types.TTeacher>) => (
                <>
                    <FormikForm noValidate autoComplete="off">
                        <FormPersonal />
                        <FormSchool
                            checkValues={checkValues}
                            setCheckValues={setCheckValues}
                            formErrors={formErrors}
                            setFormErrors={setFormErrors}
                        />
                        <FormTerms />
                        {loader === true &&
                            <LinearProgress />
                        }
                        <Box sx={loader === true ? styles.disabled : styles.action }>
                            <Button
                                variant="contained"
                                type="submit"
                                disabled={formikProps.isSubmitting || loader}
                            >
                                {GENERAL.FORM_SUBMIT}
                            </Button>
                            {
                                (!formikProps.isValid || isSaveError) && (
                                    <FormHelperText sx={styles.error}>
                                        {GENERAL.FORM_ERROR}
                                    </FormHelperText>
                                )
                            }
                        </Box>
                    </FormikForm>
                    <ClickAwayListener
                        mouseEvent="onMouseDown"
                        touchEvent="onTouchStart"
                        onClickAway={handleClickAway}
                    >
                        <Snackbar
                            open={isSnackBarOpen}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                            onClose={handleClose}
                            autoHideDuration={6000}
                        >
                            <Alert
                                onClose={handleClose}
                                severity="success"
                            >
                                {GENERAL.FORM_SUCCESS}
                            </Alert>
                        </Snackbar>
                    </ClickAwayListener>
                </>
            )}
        </Formik>
    );
};

export default Form;
